import { useAuthStore } from '@/stores';
import { fetchFTTHToken } from '@/services/api-client';

// ===== Token helpers =====
export const getToken = (): string | null => {
  return useAuthStore.getState().accessToken || null;
};

export const getRefreshToken = (): string | null => {
  return useAuthStore.getState().refreshToken || null;
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  useAuthStore.getState().setTokens(accessToken, refreshToken);
};

export const clearTokens = (): void => {
  useAuthStore.getState().logout();
};

/**
 * Refresh access token helper.
 * Uses Keycloak / OpenID refresh_token grant via POST /api/auth/Contractor/token
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const { refreshToken, setTokens } = useAuthStore.getState();
    if (!refreshToken) {
      return null;
    }

    const clientId = '53d57a7f-3f89-4e9d-873b-3d071bc6dd9f';
    const params = new URLSearchParams({
      client_id: clientId,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      scope: 'openid profile'
    });

    const res = await fetch('/api/auth/Contractor/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json, text/plain, */*'
      },
      body: params.toString()
    });

    if (res.ok) {
      const data = await res.json() as { access_token: string; refresh_token?: string };
      if (data && data.access_token) {
        const nextRefreshToken = data.refresh_token || refreshToken;
        setTokens(data.access_token, nextRefreshToken);
        return data.access_token;
      }
    }

    return null;
  } catch (err) {
    console.warn('[Auth] Token refresh error:', err);
    return null;
  }
};

/**
 * Check if user is authenticated (has a valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;

  // Check if token is expired
  if (isTokenExpired(token)) {
    return false;
  }
  return true;
};

/**
 * Decode and check JWT expiry
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = typeof atob === 'function'
      ? atob(base64)
      : typeof Buffer !== 'undefined'
        ? Buffer.from(base64, 'base64').toString('utf-8')
        : null;
    if (!jsonStr) return true;
    const decoded = JSON.parse(jsonStr);
    if (!decoded.exp) return false;
    // Consider token expired 30 seconds early to avoid edge cases
    return decoded.exp * 1000 < Date.now() + 30000;
  } catch {
    return true;
  }
};

/**
 * Check if the token is expiring soon (e.g. within 2 minutes)
 */
export const isTokenExpiringSoon = (token: string, withinSeconds = 120): boolean => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (!decoded.exp) return false;
    // Expiring soon if (expiration time < current time + buffer)
    return decoded.exp * 1000 < Date.now() + (withinSeconds * 1000);
  } catch {
    return true;
  }
};

/**
 * Get the partner ID from store (extracted from JWT AccountId)
 */
export const getPartnerId = (): number | string | null => {
  return useAuthStore.getState().partnerId;
};
